import { boolean, object, string } from "yup";
import SxForm from "../../../forms/SxForm";
import SxField from "../../../forms/fields/SxField";
import SubmitButton from "../../../components/SubmitButton";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  getCompanyOrganizationUsages,
  getOrganizationUsages,
  getSupportOrganizationUsages,
  NewUserData,
} from "../../../services/user/userModel";
import { transformEmptyToUndefined } from "../../../react-helpers/yup";
import {
  getPlanLabelKey,
  Plan,
  RecurringInterval,
} from "../../../services/subscription/subscriptionModel";
import ChoosePlan from "../../../components/ChoosePlan";
import DisplayPlan from "../../../components/DisplayPlan";
import { cx } from "../../../react-helpers/css";
import DisplayPrice from "../../../components/DisplayPrice";
import SxCheckbox from "../../../forms/fields/SxCheckbox";
import { NewOrganizationData } from "../../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";

const OrganizationRegisterForm = ({
  profileShareUid,
  plans,
  onRegister,
}: {
  profileShareUid?: string;
  plans: Plan[];
  onRegister(
    registerData: {
      user: NewUserData;
      organization: NewOrganizationData;
      profileShareUid?: string;
    },
    plan: Plan,
    recurrency: RecurringInterval,
    promoCode: string | null,
  ): Promise<any>;
}) => {
  const { t } = useTranslation(["auth", "subscription", "validation"]);
  const [searchParams] = useSearchParams();
  const organizationUsages = useMemo(() => getOrganizationUsages(t), [t]);
  const supportOrganizationUsages = useMemo(
    () => getSupportOrganizationUsages(t),
    [t],
  );
  const companyOrganizationUsages = useMemo(
    () => getCompanyOrganizationUsages(t),
    [t],
  );

  const [choosenPlan, setChoosenPlan] = useState<Plan | null>(null);
  const [choosenRecurrency, setChoosenRecurrency] =
    useState<RecurringInterval>("annual");

  const initialValues = useMemo(() => {
    return {
      firstName: "",
      lastName: "",
      email: searchParams.get("email") ?? "",
      tel: "",
      password: "",
      legalName: "",
      addressLine1: "",
      addressLine2: "",
      postalCode: "",
      city: "",
      country: "FR",
      phoneNumber: "",
      usage: "",
      promoCode: null,
      cguAccepted: false,
    };
  }, [searchParams]);

  const validationSchema = useMemo(() => {
    return object({
      firstName: string().label(t("auth:register.labels.FIRSTNAME")).required(),
      lastName: string().label(t("auth:register.labels.LASTNAME")).required(),
      email: string().email().label(t("auth:register.labels.EMAIL")).required(),
      password: string()
        .password()
        .label(t("auth:register.labels.PASSWORD"))
        .required(),
      legalName: string()
        .label(t("auth:register.labels.LEGAL_NAME"))
        .required(),
      usage: string()
        .label(t("auth:register.labels.USAGE"))
        .transform(transformEmptyToUndefined)
        .required()
        .oneOf(organizationUsages.map(([k]) => k)),
      phoneNumber: string().label(t("auth:register.labels.PHONE")),
      // promoCode: string()
      //   .label(t("auth:register.labels.PROMO_CODE"))
      //   .transform(transformEmptyToNull)
      //   .nullable(),
      cguAccepted: boolean()
        .transform((v) => !!v)
        .oneOf([true]),
    });
  }, [organizationUsages, t]);

  if (!choosenPlan)
    return (
      <div>
        <ChoosePlan
          plans={plans}
          onChoice={(plan, recurrency) => {
            setChoosenPlan(plan);
            setChoosenRecurrency(recurrency);
          }}
        />
      </div>
    );

  return (
    <div>
      <div className="lblock--l">
        <div className="grid-1-3">
          <div>
            <div className="cblock lrow --center--h">
              <div>{t("subscription:MENSUAL")}</div>
              <div>
                <button
                  type="button"
                  className={cx([
                    "btn--toggle",
                    choosenRecurrency === "monthly" && "--opt1",
                    choosenRecurrency !== "monthly" && "--opt2",
                  ])}
                  onClick={() =>
                    setChoosenRecurrency((prev) =>
                      prev === "monthly" ? "annual" : "monthly",
                    )
                  }
                />
              </div>
              <div>{t("subscription:ANNUAL")}</div>
            </div>

            <div className="cblock">
              <DisplayPlan
                plan={choosenPlan}
                recurrency={choosenRecurrency}
                cta={
                  <button
                    onClick={() => setChoosenPlan(null)}
                    className="btn--soft --block"
                  >
                    {t("auth:register.CHANGE_PLAN")}
                  </button>
                }
              />
            </div>
            <div className="cblock --txt--center">
              <Link
                className="btn--3"
                to={`/contact?object=${encodeURIComponent(t("register.NEED_A_QUOTE_MAIL_OBJECT"))}`}
              >
                {t("register.NEED_A_QUOTE_CONTACT_US")}
              </Link>
            </div>
          </div>

          <SxForm
            initialValues={initialValues}
            onSubmit={async (values) => {
              return onRegister(
                {
                  user: {
                    firstname: values.firstName,
                    lastname: values.lastName,
                    email: values.email,
                    password: values.password,
                    phoneNumber:
                      values.phoneNumber && values.phoneNumber.length > 0
                        ? values.phoneNumber
                        : null,
                    usage: values.usage,
                    searchedPost: null,
                  },
                  organization: {
                    legalName: values.legalName,
                    email: values.email,
                    phoneNumber: values.phoneNumber,
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2,
                    postalCode: values.postalCode,
                    city: values.city,
                    country: values.country,
                  },
                  profileShareUid,
                },
                choosenPlan,
                choosenRecurrency,
                values.promoCode,
              );
            }}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <div className="cblock">
              <div className="form-block">
                <h2 className="title--40">{t("auth:register.USER_ACCOUNT")}</h2>
              </div>
              <div className="grid-2 form-block">
                <SxField
                  name="firstName"
                  placeholder={t("register.placeholders.FIRSTNAME")}
                  noBlock
                />
                <SxField
                  name="lastName"
                  placeholder={t("register.placeholders.LASTNAME")}
                  noBlock
                />
              </div>
              <SxField
                name="email"
                placeholder={t("register.placeholders.EMAIL")}
              />
              <SxField
                name="phoneNumber"
                placeholder={t("register.placeholders.PHONE")}
              />
              <SxField
                name="password"
                placeholder={t("register.placeholders.PASSWORD")}
              />
            </div>

            <div className="lblock--l">
              <div className="form-block">
                <h2 className="title--40">{t("auth:register.ORGANIZATION")}</h2>
              </div>
              <div className="grid-2 form-block">
                <SxField
                  name="legalName"
                  placeholder={t("register.placeholders.LEGAL_NAME")}
                  noBlock
                />
                <SxField name="usage" as="select" noBlock>
                  <option value="">{t("register.placeholders.USAGE")}</option>
                  {!choosenPlan &&
                    organizationUsages.map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  {choosenPlan &&
                    (choosenPlan.type === "education" ||
                      choosenPlan.type === "support") &&
                    supportOrganizationUsages.map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  {choosenPlan &&
                    choosenPlan.type === "company" &&
                    companyOrganizationUsages.map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                </SxField>
              </div>
            </div>

            {choosenPlan && (
              <div className="cblock form-block">
                <div className="cblock">
                  <div>
                    <label className="field-label">
                      {t("register.labels.FORMULA")}
                    </label>
                    <select
                      className="select"
                      value={choosenRecurrency}
                      onChange={(ev) => {
                        setChoosenRecurrency(ev.target.value as any);
                      }}
                    >
                      <option value="annual">
                        {t(
                          `subscription:label.${getPlanLabelKey(choosenPlan.type)}`,
                        )}{" "}
                        (
                        <DisplayPrice
                          textOnly
                          amount={choosenPlan.stripeAnnualPrice?.amount ?? 0}
                          currency={choosenPlan.stripeAnnualPrice?.currency}
                          taxFree
                          addon={` ${t("subscription:PER_YEAR")}`}
                        />
                        )
                      </option>
                      <option value="monthly">
                        {t(
                          `subscription:label.${getPlanLabelKey(choosenPlan.type)}`,
                        )}{" "}
                        (
                        <DisplayPrice
                          textOnly
                          amount={choosenPlan.stripeMonthlyPrice?.amount ?? 0}
                          currency={choosenPlan.stripeMonthlyPrice?.currency}
                          taxFree
                          addon={` ${t("subscription:PER_MONTH")}`}
                        />
                        )
                      </option>
                    </select>
                  </div>

                  {/* <SxField name="promoCode" noBlock /> */}
                </div>

                <div className="cblock">
                  <SxCheckbox
                    name="cguAccepted"
                    label={
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t("auth:register.ACCEPT_CGU"),
                        }}
                      />
                    }
                  />
                </div>
              </div>
            )}

            <div className="lblock--l --txt--center">
              <SubmitButton type="submit" className="btn--2">
                {t("register.SUBSCRIBE")}
              </SubmitButton>
            </div>
          </SxForm>
        </div>
      </div>
    </div>
  );
};

export default OrganizationRegisterForm;
